import { request } from "react-rb/request"
import { camelizeKeys } from "core/utils/camelize-keys"

/**
 * Appends filter parameters to a URLSearchParams under the `filters` scope,
 * matching the nested `params.require(:filters).permit(...)` shape the reports
 * controllers expect. Array values become repeated `filters[key][]` entries;
 * empty arrays contribute nothing, so an unset axis is absent rather than blank.
 * @param {URLSearchParams} searchParams - Target params, mutated in place
 * @param {Object} [filters] - Map of filter axis key to value or array of values
 */
function appendFilterParams(searchParams, filters = {}) {
  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return

    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(`filters[${key}][]`, item))
    } else {
      searchParams.append(`filters[${key}]`, value)
    }
  })
}

/**
 * Fetches broadcast email performance data for signals
 * @param {string} type - The type of signal data to fetch (e.g., "broadcast", "time")
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { countries: ['US', 'CA'], tags: ['tag1'] })
 * @returns {Promise<Object>} Broadcast email performance data
 */
export async function fetchRecentPerformance(type = "broadcast", filters = {}) {
  const searchParams = new URLSearchParams()
  searchParams.append("type", type)

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/recent_performance${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches subscriber engagement data for signals
 * @param {number} engagementPeriod - Period of calculation (15 or 30 days)
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { countries: ['US', 'CA'], tags: ['tag1'] })
 * @returns {Promise<Object>} Subscriber engagement data
 */
export async function fetchSubscriberEngagement(
  engagementPeriod = 15,
  filters = {}
) {
  const searchParams = new URLSearchParams()
  searchParams.append("engagement_period", engagementPeriod)

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/engaged_subscribers${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches cohort data for signals
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { countries: ['US', 'CA'], tags: ['tag1'] })
 * @returns {Promise<Object>} Cohort data
 */
export async function fetchCohortData(filters = {}) {
  const searchParams = new URLSearchParams()

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/cohorts${queryString ? `?${queryString}` : ""}`

  const response = await request(url, {
    method: "GET"
  })

  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches summary metrics data for signals
 * @param {number} [days] - Number of days to include (default: 90)
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { countries: ['US', 'CA'], tags: ['tag1'] })
 * @returns {Promise<Object>} Summary metrics data (totalRecipients, openRate, clickRate, unsubscribedRate)
 */
export async function fetchSummaryMetrics(days, filters = {}) {
  const searchParams = new URLSearchParams()
  if (days) {
    searchParams.append("days", days)
  }

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/summary_metrics${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches average subscriber lifetime data for signals
 * @param {Object} [filters] - Filter parameters to include in the request
 * @returns {Promise<Object>} Avg lifetime data
 */
export async function fetchAvgLifetime(filters = {}) {
  const searchParams = new URLSearchParams()

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/avg_lifetime${queryString ? `?${queryString}` : ""}`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

export async function fetchSubscribersGrowth(filters = {}) {
  const searchParams = new URLSearchParams()

  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/subscribers_growth${queryString ? `?${queryString}` : ""}`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the demographics summary metrics (dominant gender, median age, median
 * household income, high earners, influential subscribers).
 *
 * Every metric arrives with its value keys always present plus an
 * `insufficientData` boolean, so callers branch on that one flag rather than on
 * the shape of the payload.
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} Demographics summary metrics data
 */
export async function fetchDemographicsSummaryMetrics(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/summary_metrics${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the age bracket distribution for the demographics dashboard.
 * Below the low-data threshold the payload is
 * `{ insufficientData: true, total, buckets: [] }`. A filter that matches
 * nobody returns the same empty series with `insufficientData: false`.
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<{insufficientData: boolean, total: number, buckets: Array}>}
 */
export async function fetchAgeDistribution(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/age_distribution${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the household-income display-bucket distribution for the
 * demographics dashboard. Below the low-data threshold the payload is
 * `{ insufficientData: true, total: 0, buckets: [] }`.
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<{insufficientData: boolean, total: number, buckets: Array}>}
 */
export async function fetchHouseholdIncomeDistribution(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/household_income_distribution${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the education level distribution for the demographics dashboard
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} `{ insufficientData, total, buckets: [{ value, label, count }] }`
 */
export async function fetchEducationLevelDistribution(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/education_level_distribution${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the follower bracket distribution for the demographics dashboard.
 * Below the low-data threshold the payload is
 * `{ insufficientData: true, total, buckets: [] }`. A filter that matches
 * nobody returns the same empty series with `insufficientData: false`.
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<{insufficientData: boolean, total: number, buckets: Array}>}
 */
export async function fetchFollowerDistribution(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/follower_distribution${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the top job titles for the demographics dashboard
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} `{ entries: [{ label, count, percentage }], total }`
 */
export async function fetchTopJobTitles(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/top_job_titles${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the top countries by subscriber count for the demographics dashboard
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} `{ entries: [{ value, label, count }], total }`
 */
export async function fetchTopCountries(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/top_countries${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the top cities by subscriber count for the demographics dashboard
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} `{ entries: [{ value, label, count }], total }`
 */
export async function fetchTopCities(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/top_cities${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the social platform presence for the demographics dashboard
 * @param {Object} [filters] - Filter parameters to include in the request (e.g., { tags: ['tag1'], statuses: ['active'] })
 * @returns {Promise<Object>} `{ entries: [{ platform, label, count, percentage }], total }`
 */
export async function fetchSocialPlatformPresence(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/social_platform_presence${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the subscribers to watch for the dashboard widget — subscribers who
 * joined recently, match at least one predefined signals segment, and have a
 * complete profile — sorted by social reach descending.
 * @returns {Promise<Object>} `{ subscribers: [{ id, firstName, emailAddress, createdAt, joinedAt, profilePicture, bio, socialReach, profileUrls: [{ platform, url }], segments }], totalCount }`
 */
export async function fetchSubscribersToWatch() {
  const response = await request("/reports/signals/subscribers_to_watch", {
    method: "GET"
  })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Persists that the creator closed the dashboard widget's dismissible note,
 * whichever Signals state produced it. Resolves with nothing: the endpoint
 * answers 204.
 * @returns {Promise<void>}
 */
export async function dismissSubscribersToWatch() {
  const response = await request(
    "/reports/signals/subscribers_to_watch/dismiss",
    { method: "PUT" }
  )
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }
}

/**
 * Fetches location filters for signals
 * @returns {Promise<Object>} Location filters
 */
export async function fetchLocationFilters() {
  const response = await request("/reports/signals/filters/locations", {
    method: "GET"
  })

  return camelizeKeys(response.data)
}

export async function fetchTagFilters() {
  const response = await request("/reports/signals/filters/tags", {
    method: "GET"
  })

  return camelizeKeys(response.data)
}

/**
 * Fetches the enumerated Kit Field filter options (gender, age, household
 * income, education level, occupation, platform, follower count). The backend
 * is the single source of these lists and only returns the axes enabled for
 * the account, so the response doubles as the list of Kit Field filters to
 * offer.
 * @returns {Promise<Object>} Map of filter axis key to [{ value, label }]
 */
export async function fetchKitFieldFilterOptions() {
  const response = await request("/reports/signals/filters/kit_field_options", {
    method: "GET"
  })

  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return response.data
}

/**
 * Fetches a paginated page of tag filters for signals with optional search
 * @param {Object} [options]
 * @param {string} [options.after] - Cursor for the next page
 * @param {number} [options.perPage=100] - Page size
 * @param {string} [options.searchTerm] - Substring filter for tag name
 * @returns {Promise<{tags: Array<{value: string, label: string}>, pagination: Object}>}
 */
export async function fetchSubscribersGrowthBreakdown(
  metric,
  breakdown,
  filters = {}
) {
  const searchParams = new URLSearchParams()
  searchParams.append("metric", metric)
  searchParams.append("breakdown", breakdown)

  appendFilterParams(searchParams, filters)

  const url = `/reports/signals/subscribers_growth_breakdown?${searchParams.toString()}`
  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

export async function fetchTagFiltersPaginated({
  after,
  perPage = 100,
  searchTerm
} = {}) {
  const params = new URLSearchParams()
  if (after != null) params.set("after", after)
  if (perPage != null) params.set("per_page", perPage)
  if (searchTerm != null) params.set("search_term", searchTerm)

  const response = await request(
    `/reports/signals/filters/tags?${params.toString()}`,
    { method: "GET" }
  )

  return camelizeKeys(response.data)
}

/**
 * Fetches gender distribution for the demographics donut chart
 * @param {Object} [filters] - Filter parameters to include in the request
 * @returns {Promise<{insufficientData: boolean, total: number, distribution: Array}>}
 */
export async function fetchGenderDistribution(filters = {}) {
  const searchParams = new URLSearchParams()
  appendFilterParams(searchParams, filters)

  const queryString = searchParams.toString()
  const url = `/reports/signals/demographics/gender_distribution${
    queryString ? `?${queryString}` : ""
  }`

  const response = await request(url, { method: "GET" })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Fetches the account's notification preferences for every active predefined
 * signals segment. Segments without a stored preference come back enabled.
 * @returns {Promise<Object>} `{ notificationPreferences: [{ slug, label, definition, enabled }] }`
 */
export async function fetchNotificationPreferences() {
  const response = await request("/signals/notification_preferences", {
    method: "GET"
  })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

/**
 * Turns notifications for one predefined signals segment on or off.
 *
 * PUT rather than PATCH: `request` lowercases the method before handing it to
 * fetch, and fetch only re-uppercases the classic verbs (GET, POST, PUT,
 * DELETE, HEAD, OPTIONS). A lowercase `patch` reaches nginx verbatim, which
 * rejects it with a 400 before Rails sees the request. The route accepts both.
 * @param {Object} params
 * @param {string} params.slug - Predefined segment slug
 * @param {boolean} params.enabled - Whether the segment should notify
 * @returns {Promise<Object>} The updated `{ slug, label, definition, enabled }`
 */
export async function updateNotificationPreference({ slug, enabled }) {
  const response = await request(
    `/signals/notification_preferences/${encodeURIComponent(slug)}`,
    { method: "PUT", data: { enabled } }
  )
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }

  return camelizeKeys(response.data)
}

export async function optIntoSignals(termsVersion) {
  const response = await request("/signals/enrollments", {
    method: "POST",
    data: { terms_version: termsVersion }
  })
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`)
    error.response = response
    throw error
  }
}
